'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * pa-1 (PA1-5) — quando `false`, Escape e clique no backdrop **não** fecham.
   *
   * Existe porque um modal pode chegar a um estado em que fechar destrói algo que não volta —
   * o caso concreto é a senha temporária exibida uma única vez. O botão de fechar permanece:
   * a intenção de sair continua disponível, só deixa de acontecer por acidente.
   */
  dismissable?: boolean;
}

// max-width sempre aplicado: com o portal, a overlay já ocupa a viewport toda,
// então `w-full` + `max-w-*` + padding da overlay = diálogo contido e centrado.
// No mobile (<max-w) vira ~w-full com as margens do padding; no desktop, capado.
const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-5xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({ open, onClose, title, children, footer, size = 'md', dismissable = true }: ModalProps) {
  // id único por instância — evita colisão de aria-labelledby quando há vários
  // modais montados na mesma página.
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // Elemento que tinha o foco antes de abrir — para restaurá-lo ao fechar.
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Portal só após montar no cliente (evita mismatch SSR e garante document).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open || !mounted) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;

    // Elementos focáveis visíveis dentro do modal.
    const focusables = () =>
      panel
        ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
            (el) => el.offsetParent !== null,
          )
        : [];

    // Foco inicial: primeiro focável do modal (ou o próprio painel).
    (focusables()[0] ?? panel)?.focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dismissable) onClose();   // pa-1 (PA1-5)
        return;
      }
      if (e.key !== 'Tab') return;
      // Focus trap: cicla o Tab/Shift+Tab dentro do diálogo.
      const els = focusables();
      if (els.length === 0) {
        e.preventDefault();
        panel?.focus();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    // Trava o scroll no <html> (root scroller), NÃO no <body>: o body aqui é
    // `flex flex-col` e travar overflow nele dispara reflow que expande a página
    // por trás no Android Chrome. html não é flex → sem reflow horizontal.
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.documentElement.style.overflow = '';
      // Restaura o foco ao elemento que abriu o modal.
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose, mounted, dismissable]);   // pa-1 (PA1-5): sem isto o handler capturaria o valor antigo

  if (!open || !mounted) return null;

  // Portal para o body: o overlay `fixed` fica sempre relativo à viewport,
  // imune a qualquer containing-block (transform/filter/contain) de ancestrais
  // — corrige o modal "encolhido" / fora de escala no mobile.
  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      onClick={dismissable ? onClose : undefined}
    >
      {/* Painel contido na viewport: header e footer fixos (sempre acessíveis — o X
          nunca fica fora da tela no mobile); só o miolo rola. */}
      <div
        ref={panelRef}
        className={`${SIZE_CLASSES[size]} flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 id={titleId} className="text-lg font-semibold text-gray-900">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-6 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
